/**
 * 自由拼接画板组件 - 使用 Konva 画布框架
 * 用户可以自由拖拽、缩放、旋转图片到画布任意位置
 * 支持画笔绘图功能
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Transformer, Line } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import { 
  X, Plus, Trash2, RotateCcw, 
  Check, Layers, ZoomIn, ZoomOut,
  UploadCloud, Image as ImageIcon, Loader2, 
  Move, Maximize2, Pencil, Eraser, MousePointer
} from 'lucide-react';

// 图片项类型
interface ImageItem {
  id: string;
  file: File;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
}

// 画线类型
interface DrawLine {
  id: string;
  points: number[];
  stroke: string;
  strokeWidth: number;
}

// 可拖拽图片组件
const DraggableImage: React.FC<{
  imageItem: ImageItem;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (newAttrs: Partial<ImageItem>) => void;
  isDraggable: boolean;
  previewScale: number;
}> = ({ imageItem, isSelected, onSelect, onChange, isDraggable, previewScale }) => {
  const [image] = useImage(imageItem.url);
  const imageRef = useRef<Konva.Image>(null);
  const transformerRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (isSelected && transformerRef.current && imageRef.current) {
      transformerRef.current.nodes([imageRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  if (!image) return null;

  // 在预览视图中的位置和大小
  const previewX = imageItem.x * previewScale;
  const previewY = imageItem.y * previewScale;
  const previewWidth = imageItem.width * previewScale;
  const previewHeight = imageItem.height * previewScale;

  return (
    <>
      <KonvaImage
        ref={imageRef}
        image={image}
        x={previewX}
        y={previewY}
        width={previewWidth}
        height={previewHeight}
        scaleX={imageItem.scaleX}
        scaleY={imageItem.scaleY}
        rotation={imageItem.rotation}
        draggable={isDraggable}
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => {
          // 转换回原始坐标
          onChange({
            x: e.target.x() / previewScale,
            y: e.target.y() / previewScale,
          });
        }}
        onTransformEnd={() => {
          const node = imageRef.current;
          if (!node) return;
          
          onChange({
            x: node.x() / previewScale,
            y: node.y() / previewScale,
            scaleX: node.scaleX(),
            scaleY: node.scaleY(),
            rotation: node.rotation(),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={transformerRef}
          boundBoxFunc={(oldBox, newBox) => {
            // 最小尺寸限制
            const minSize = 20 * previewScale;
            if (newBox.width < minSize || newBox.height < minSize) {
              return oldBox;
            }
            return newBox;
          }}
          rotateEnabled={true}
          keepRatio={false}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right', 'middle-left', 'middle-right', 'top-center', 'bottom-center']}
          anchorSize={12}
          anchorCornerRadius={3}
          anchorStroke="#D4AF37"
          anchorFill="#FFF8DC"
          anchorStrokeWidth={2}
          borderStroke="#D4AF37"
          borderStrokeWidth={2}
          borderDash={[6, 4]}
          padding={5}
        />
      )}
    </>
  );
};

// 工具类型
type ToolType = 'select' | 'draw' | 'eraser';

interface ImageComposerProps {
  onCompose: (composedImageBlob: Blob) => void;
  onClose: () => void;
  resolution: 'horizontal' | 'vertical';
}

const ImageComposer: React.FC<ImageComposerProps> = ({ onCompose, onClose, resolution }) => {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [canvasSize] = useState({ 
    width: resolution === 'horizontal' ? 1920 : 1080, 
    height: resolution === 'horizontal' ? 1080 : 1920 
  });
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [isGenerating, setIsGenerating] = useState(false);
  
  // 绘图相关状态
  const [tool, setTool] = useState<ToolType>('select');
  const [lines, setLines] = useState<DrawLine[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(5);
  
  const stageRef = useRef<Konva.Stage>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 450 });

  const generateId = () => Math.random().toString(36).substr(2, 9);

  // 计算预览尺寸
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth - 64;
        const containerHeight = containerRef.current.clientHeight - 64;
        const ratio = canvasSize.width / canvasSize.height;
        
        let width = containerWidth;
        let height = width / ratio;
        
        if (height > containerHeight) {
          height = containerHeight;
          width = height * ratio;
        }
        
        setStageSize({ width, height });
      }
    };
    
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [canvasSize]);

  // 处理图片上传
  const handleImageUpload = useCallback((files: FileList | null) => {
    if (!files) return;
    
    Array.from(files).forEach((file, index) => {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      
      img.onload = () => {
        const maxSize = Math.min(canvasSize.width, canvasSize.height) * 0.4;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width *= ratio;
          height *= ratio;
        }
        
        const offsetX = (index % 3) * 100 + 100;
        const offsetY = Math.floor(index / 3) * 100 + 100;
        
        const item: ImageItem = {
          id: generateId(),
          file,
          url,
          x: canvasSize.width / 2 - width / 2 + offsetX,
          y: canvasSize.height / 2 - height / 2 + offsetY,
          width,
          height,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
        };
        
        setImages(prev => [...prev, item]);
      };
      
      img.src = url;
    });
  }, [canvasSize]);

  // 删除图片
  const removeImage = (id: string) => {
    const img = images.find(i => i.id === id);
    if (img) {
      URL.revokeObjectURL(img.url);
    }
    setImages(prev => prev.filter(i => i.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
    }
  };

  // 清空所有图片和画线
  const clearAllImages = () => {
    images.forEach(img => URL.revokeObjectURL(img.url));
    setImages([]);
    setLines([]);
    setSelectedId(null);
  };

  // 更新图片属性
  const updateImage = (id: string, newAttrs: Partial<ImageItem>) => {
    setImages(prev => prev.map(img =>
      img.id === id ? { ...img, ...newAttrs } : img
    ));
  };

  // 缩放比例
  const previewScale = stageSize.width / canvasSize.width;

  // 获取画布上的真实坐标
  const getPointerPosition = () => {
    const stage = stageRef.current;
    if (!stage) return null;
    const pos = stage.getPointerPosition();
    if (!pos) return null;
    return {
      x: pos.x / previewScale,
      y: pos.y / previewScale,
    };
  };

  // 开始绘画
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    // 检查是否点击了 Transformer 的控制点或边框
    const target = e.target;
    const isTransformerPart = target.getParent()?.getClassName() === 'Transformer' || 
                              target.getClassName() === 'Transformer';
    
    // 如果点击的是 Transformer 的一部分，不做任何处理（让 Transformer 处理）
    if (isTransformerPart) {
      return;
    }
    
    // 如果点击的是 Stage 背景（只有背景矩形），取消选中
    const isBackgroundRect = target === e.target.getStage() || 
                             (target.getClassName() === 'Rect' && target.name() === 'background');
    
    if (tool === 'select') {
      if (isBackgroundRect) {
        setSelectedId(null);
      }
      return;
    }
    
    // 绘画模式
    if (tool === 'draw' || tool === 'eraser') {
      setSelectedId(null); // 绘画时取消选中
      setIsDrawing(true);
      const pos = getPointerPosition();
      if (!pos) return;
      
      const newLine: DrawLine = {
        id: generateId(),
        points: [pos.x, pos.y],
        stroke: tool === 'eraser' ? bgColor : brushColor,
        strokeWidth: tool === 'eraser' ? brushSize * 3 : brushSize,
      };
      setLines(prev => [...prev, newLine]);
    }
  };

  // 绘画中
  const handleMouseMove = () => {
    if (!isDrawing) return;
    if (tool !== 'draw' && tool !== 'eraser') return;
    
    const pos = getPointerPosition();
    if (!pos) return;
    
    setLines(prev => {
      const lastLine = prev[prev.length - 1];
      if (!lastLine) return prev;
      
      const newPoints = [...lastLine.points, pos.x, pos.y];
      const newLines = prev.slice(0, -1);
      return [...newLines, { ...lastLine, points: newPoints }];
    });
  };

  // 结束绘画
  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  // 撤销最后一条线
  const undoLine = () => {
    setLines(prev => prev.slice(0, -1));
  };

  // 清除所有画线
  const clearLines = () => {
    setLines([]);
  };

  // 生成最终图片
  const generateComposedImage = async () => {
    if (images.length === 0 && lines.length === 0) return;
    
    setIsGenerating(true);
    
    try {
      const prevSelectedId = selectedId;
      setSelectedId(null);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const stage = stageRef.current;
      if (!stage) return;

      const dataURL = stage.toDataURL({
        pixelRatio: canvasSize.width / stageSize.width,
        mimeType: 'image/jpeg',
        quality: 0.95,
      });
      
      setSelectedId(prevSelectedId);

      const res = await fetch(dataURL);
      const blob = await res.blob();
      
      onCompose(blob);
      setIsGenerating(false);
      
    } catch (error) {
      console.error('生成图片失败:', error);
      setIsGenerating(false);
    }
  };

  // 图层操作
  const bringToFront = (id: string) => {
    setImages(prev => {
      const index = prev.findIndex(img => img.id === id);
      if (index === -1 || index === prev.length - 1) return prev;
      const newImages = [...prev];
      const [img] = newImages.splice(index, 1);
      newImages.push(img);
      return newImages;
    });
  };

  const sendToBack = (id: string) => {
    setImages(prev => {
      const index = prev.findIndex(img => img.id === id);
      if (index <= 0) return prev;
      const newImages = [...prev];
      const [img] = newImages.splice(index, 1);
      newImages.unshift(img);
      return newImages;
    });
  };

  const selectedImage = images.find(img => img.id === selectedId);

  // 预设颜色
  const presetColors = ['#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#8B4513', '#D4AF37'];

  return (
    <div className="fixed inset-0 z-50 flex flex-col animate-in fade-in duration-200" style={{
      backgroundImage: `url('/ai漫剧网站背景图.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <div className="absolute inset-0 bg-amber-950/40 backdrop-blur-[2px]" />
      
      {/* 顶部工具栏 */}
      <header className="relative h-16 flex items-center justify-between px-6 shrink-0" style={{
        background: 'linear-gradient(to bottom, rgba(139,69,19,0.95), rgba(101,67,33,0.9))',
        borderBottom: '3px solid',
        borderImage: 'linear-gradient(to right, #8B4513, #D4AF37, #8B4513) 1',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
      }}>
        <div className="flex items-center gap-5">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-amber-200/80 hover:text-amber-100 transition-colors group"
          >
            <X size={22} className="group-hover:rotate-90 transition-transform duration-300" />
            <span className="text-sm font-medium" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>返回</span>
          </button>
          <div className="h-8 w-px bg-gradient-to-b from-transparent via-amber-600/50 to-transparent" />
          <h2 className="text-amber-100 text-lg tracking-wider" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
            🎨 自由拼图画板
          </h2>
          <span className="text-xs text-amber-300/60 px-2 py-0.5 rounded bg-amber-900/30 border border-amber-700/30">
            {resolution === 'horizontal' ? '横屏 16:9' : '竖屏 9:16'}
          </span>
        </div>
        
        {/* 工具选择 */}
        <div className="flex items-center gap-2 bg-amber-900/40 rounded-lg p-1 border border-amber-700/40">
          <button
            onClick={() => setTool('select')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
              tool === 'select' 
                ? 'bg-amber-700/60 text-amber-100' 
                : 'text-amber-300/70 hover:text-amber-200 hover:bg-amber-800/40'
            }`}
            style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}
          >
            <MousePointer size={16} />
            选择
          </button>
          <button
            onClick={() => setTool('draw')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
              tool === 'draw' 
                ? 'bg-amber-700/60 text-amber-100' 
                : 'text-amber-300/70 hover:text-amber-200 hover:bg-amber-800/40'
            }`}
            style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}
          >
            <Pencil size={16} />
            画笔
          </button>
          <button
            onClick={() => setTool('eraser')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
              tool === 'eraser' 
                ? 'bg-amber-700/60 text-amber-100' 
                : 'text-amber-300/70 hover:text-amber-200 hover:bg-amber-800/40'
            }`}
            style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}
          >
            <Eraser size={16} />
            橡皮
          </button>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={clearAllImages}
            disabled={images.length === 0 && lines.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm text-amber-200/80 hover:text-amber-100 bg-amber-900/40 hover:bg-amber-800/50 border border-amber-700/40 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}
          >
            <Trash2 size={16} />
            清空
          </button>
          <button
            onClick={generateComposedImage}
            disabled={(images.length === 0 && lines.length === 0) || isGenerating}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              fontFamily: '"ZCOOL XiaoWei", serif',
              background: 'linear-gradient(135deg, #8B0000, #B22222)',
              color: '#FFEFD5',
              border: '1px solid #D4AF37',
              boxShadow: '0 2px 10px rgba(139,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)'
            }}
          >
            {isGenerating ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <Check size={18} />
                完成
              </>
            )}
          </button>
        </div>
      </header>

      <div className="relative flex-1 flex overflow-hidden">
        {/* 左侧面板 */}
        <aside className="w-72 flex flex-col shrink-0" style={{
          background: 'linear-gradient(to right, rgba(139,90,43,0.95), rgba(160,120,70,0.9))',
          borderRight: '3px solid',
          borderImage: 'linear-gradient(to bottom, #8B4513, #D4AF37, #8B4513) 1',
          boxShadow: '4px 0 20px rgba(0,0,0,0.3)'
        }}>
          {/* 画笔设置 */}
          {(tool === 'draw' || tool === 'eraser') && (
            <div className="p-4 border-b border-amber-800/40">
              <h3 className="text-sm font-medium text-amber-100 mb-3 flex items-center gap-2" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
                <Pencil size={14} className="text-amber-300" />
                画笔设置
              </h3>
              
              {tool === 'draw' && (
                <div className="mb-4">
                  <span className="text-xs text-amber-200/80 block mb-2">画笔颜色</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {presetColors.map(color => (
                      <button
                        key={color}
                        onClick={() => setBrushColor(color)}
                        className={`w-7 h-7 rounded-md border-2 transition-all ${
                          brushColor === color ? 'border-amber-300 scale-110' : 'border-amber-700/50 hover:border-amber-500'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                    <input
                      type="color"
                      value={brushColor}
                      onChange={(e) => setBrushColor(e.target.value)}
                      className="w-7 h-7 rounded-md cursor-pointer border-2 border-amber-700/50"
                    />
                  </div>
                </div>
              )}
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-amber-200/80">{tool === 'eraser' ? '橡皮大小' : '画笔粗细'}</span>
                  <span className="text-xs text-amber-300 font-mono bg-amber-900/40 px-2 py-0.5 rounded">{brushSize}px</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="w-full h-1.5 bg-amber-900/50 rounded-lg appearance-none cursor-pointer"
                  style={{ accentColor: '#D4AF37' }}
                />
              </div>
              
              <div className="flex gap-2 mt-4">
                <button
                  onClick={undoLine}
                  disabled={lines.length === 0}
                  className="flex-1 py-2 text-xs text-amber-200/80 hover:text-amber-100 bg-amber-900/40 hover:bg-amber-800/50 border border-amber-700/40 rounded-lg transition-all disabled:opacity-40 flex items-center justify-center gap-1"
                >
                  <RotateCcw size={12} />
                  撤销
                </button>
                <button
                  onClick={clearLines}
                  disabled={lines.length === 0}
                  className="flex-1 py-2 text-xs text-amber-200/80 hover:text-amber-100 bg-amber-900/40 hover:bg-amber-800/50 border border-amber-700/40 rounded-lg transition-all disabled:opacity-40 flex items-center justify-center gap-1"
                >
                  <Trash2 size={12} />
                  清除画线
                </button>
              </div>
            </div>
          )}
          
          {/* 背景颜色设置 */}
          <div className="p-4 border-b border-amber-800/40">
            <h3 className="text-sm font-medium text-amber-100 mb-3 flex items-center gap-2" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
              <ImageIcon size={14} className="text-amber-300" />
              画布背景
            </h3>
            <div className="flex gap-2 flex-wrap">
              {['#FFFFFF', '#F5F5F5', '#FFFAF0', '#FFF8DC', '#000000', '#2C1810'].map(color => (
                <button
                  key={color}
                  onClick={() => setBgColor(color)}
                  className={`w-9 h-9 rounded-lg border-2 transition-all ${
                    bgColor === color ? 'border-amber-300 scale-110 shadow-lg' : 'border-amber-700/50 hover:border-amber-500'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="w-9 h-9 rounded-lg cursor-pointer border-2 border-amber-700/50"
              />
            </div>
          </div>

          {/* 图片列表 */}
          <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-amber-100 flex items-center gap-2" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
                <Layers size={14} className="text-amber-300" />
                图片素材 <span className="text-amber-400/80">({images.length})</span>
              </h3>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-amber-300 hover:text-amber-100 flex items-center gap-1 px-2 py-1 rounded bg-amber-900/30 hover:bg-amber-800/40 border border-amber-700/30 transition-all"
              >
                <Plus size={12} />
                添加
              </button>
            </div>

            {images.length === 0 ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-amber-600/40 rounded-xl p-6 flex flex-col items-center justify-center text-amber-300/60 hover:border-amber-400 hover:text-amber-200 transition-all cursor-pointer bg-amber-950/20 hover:bg-amber-900/30"
              >
                <UploadCloud size={32} className="mb-2 opacity-60" />
                <span className="text-sm" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>点击上传图片</span>
              </div>
            ) : (
              <div className="space-y-2">
                {images.map((img, index) => (
                  <div
                    key={img.id}
                    className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-all cursor-pointer ${
                      selectedId === img.id
                        ? 'border-amber-400 bg-amber-900/40 shadow-lg'
                        : 'border-amber-700/30 hover:border-amber-500/50 bg-amber-950/30 hover:bg-amber-900/30'
                    }`}
                    onClick={() => { setTool('select'); setSelectedId(img.id); }}
                  >
                    <img
                      src={img.url}
                      alt={`图片 ${index + 1}`}
                      className="w-10 h-10 object-cover rounded border border-amber-700/40"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-amber-100 truncate">{img.file.name}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                      className="p-1 text-amber-400/60 hover:text-red-400 transition-colors rounded hover:bg-red-900/30"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-amber-600/40 rounded-lg text-amber-300/60 hover:border-amber-400 hover:text-amber-200 transition-all flex items-center justify-center gap-2 text-xs bg-amber-950/20 hover:bg-amber-900/30"
                >
                  <Plus size={14} />
                  添加图片
                </button>
              </div>
            )}
          </div>

          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              handleImageUpload(e.target.files);
              e.target.value = '';
            }}
          />
        </aside>

        {/* 中间画布区 */}
        <main className="flex-1 flex items-center justify-center p-8" ref={containerRef} style={{
          background: 'radial-gradient(ellipse at center, rgba(139,90,43,0.2), rgba(60,40,20,0.4))'
        }}>
          <div className="relative" style={{
            boxShadow: '0 0 40px rgba(139,69,19,0.4), 0 10px 40px rgba(0,0,0,0.5)'
          }}>
            <div className="absolute -inset-3 rounded-xl pointer-events-none" style={{
              border: '2px solid rgba(212,175,55,0.3)',
            }} />
            
            <Stage
              ref={stageRef}
              width={stageSize.width}
              height={stageSize.height}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              style={{ 
                borderRadius: '8px', 
                overflow: 'hidden',
                cursor: tool === 'draw' ? 'crosshair' : tool === 'eraser' ? 'cell' : 'default'
              }}
            >
              <Layer>
                {/* 背景 */}
                <Rect
                  name="background"
                  x={0}
                  y={0}
                  width={stageSize.width}
                  height={stageSize.height}
                  fill={bgColor}
                />
                
                {/* 图片层 */}
                {images.map((img) => (
                  <DraggableImage
                    key={img.id}
                    imageItem={img}
                    isSelected={selectedId === img.id}
                    onSelect={() => { if (tool === 'select') setSelectedId(img.id); }}
                    onChange={(newAttrs) => updateImage(img.id, newAttrs)}
                    isDraggable={tool === 'select'}
                    previewScale={previewScale}
                  />
                ))}
                
                {/* 画线层 - 也需要缩放坐标 */}
                {lines.map((line) => (
                  <Line
                    key={line.id}
                    points={line.points.map((p, i) => p * previewScale)}
                    stroke={line.stroke}
                    strokeWidth={line.strokeWidth * previewScale}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation="source-over"
                  />
                ))}
              </Layer>
            </Stage>

            {/* 空状态提示 */}
            {images.length === 0 && lines.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-amber-300/50 pointer-events-none">
                <ImageIcon size={56} className="opacity-40 mb-4" />
                <p className="text-base" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>上传图片或使用画笔绘制</p>
              </div>
            )}

            {/* 操作提示 */}
            {(images.length > 0 || lines.length > 0) && (
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-xs px-5 py-2.5 rounded-full flex items-center gap-4 pointer-events-none" style={{
                background: 'linear-gradient(to right, rgba(139,69,19,0.9), rgba(101,67,33,0.9))',
                border: '1px solid rgba(212,175,55,0.4)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                fontFamily: '"ZCOOL XiaoWei", serif'
              }}>
                {tool === 'select' ? (
                  <>
                    <span className="flex items-center gap-1.5 text-amber-200">
                      <Move size={14} className="text-amber-400" />
                      拖拽移动
                    </span>
                    <span className="text-amber-600">•</span>
                    <span className="flex items-center gap-1.5 text-amber-200">
                      <Maximize2 size={14} className="text-amber-400" />
                      拖拽角点缩放
                    </span>
                  </>
                ) : (
                  <span className="text-amber-200">
                    {tool === 'draw' ? '🖌️ 按住鼠标绘制' : '🧹 按住鼠标擦除'}
                  </span>
                )}
              </div>
            )}
          </div>
        </main>

        {/* 右侧编辑面板 */}
        {selectedImage && tool === 'select' && (
          <aside className="w-64 p-4 shrink-0" style={{
            background: 'linear-gradient(to left, rgba(139,90,43,0.95), rgba(160,120,70,0.9))',
            borderLeft: '3px solid',
            borderImage: 'linear-gradient(to bottom, #8B4513, #D4AF37, #8B4513) 1',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.3)'
          }}>
            <h3 className="text-sm font-medium text-amber-100 mb-4 flex items-center gap-2" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
              <ZoomIn size={16} className="text-amber-300" />
              图片调整
            </h3>
            
            <div className="space-y-4">
              {/* 预览 */}
              <div className="aspect-video rounded-lg overflow-hidden border-2 border-amber-700/40" style={{ background: 'rgba(44,24,16,0.6)' }}>
                <img src={selectedImage.url} alt="选中图片" className="w-full h-full object-contain" />
              </div>

              {/* 旋转 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-amber-200/80">旋转</span>
                  <span className="text-xs text-amber-300 font-mono bg-amber-900/40 px-2 py-0.5 rounded">{Math.round(selectedImage.rotation)}°</span>
                </div>
                <input
                  type="range"
                  min="-180"
                  max="180"
                  value={selectedImage.rotation}
                  onChange={(e) => updateImage(selectedImage.id, { rotation: Number(e.target.value) })}
                  className="w-full h-1.5 bg-amber-900/50 rounded-lg appearance-none cursor-pointer"
                  style={{ accentColor: '#D4AF37' }}
                />
              </div>

              {/* 缩放 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-amber-200/80">缩放</span>
                  <span className="text-xs text-amber-300 font-mono bg-amber-900/40 px-2 py-0.5 rounded">{Math.round(selectedImage.scaleX * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="300"
                  value={selectedImage.scaleX * 100}
                  onChange={(e) => {
                    const scale = Number(e.target.value) / 100;
                    updateImage(selectedImage.id, { scaleX: scale, scaleY: scale });
                  }}
                  className="w-full h-1.5 bg-amber-900/50 rounded-lg appearance-none cursor-pointer"
                  style={{ accentColor: '#D4AF37' }}
                />
              </div>

              {/* 快捷按钮 */}
              <div className="flex gap-2">
                <button
                  onClick={() => bringToFront(selectedImage.id)}
                  className="flex-1 py-2 text-xs text-amber-200/80 hover:text-amber-100 bg-amber-900/40 hover:bg-amber-800/50 border border-amber-700/40 rounded-lg transition-all"
                >
                  置顶
                </button>
                <button
                  onClick={() => sendToBack(selectedImage.id)}
                  className="flex-1 py-2 text-xs text-amber-200/80 hover:text-amber-100 bg-amber-900/40 hover:bg-amber-800/50 border border-amber-700/40 rounded-lg transition-all"
                >
                  置底
                </button>
              </div>

              {/* 重置 */}
              <button
                onClick={() => {
                  updateImage(selectedImage.id, { 
                    scaleX: 1, scaleY: 1, rotation: 0,
                    x: canvasSize.width / 2 - selectedImage.width / 2,
                    y: canvasSize.height / 2 - selectedImage.height / 2,
                  });
                }}
                className="w-full py-2 text-xs text-amber-200/80 hover:text-amber-100 bg-amber-900/40 hover:bg-amber-800/50 border border-amber-700/40 rounded-lg transition-all flex items-center justify-center gap-1"
              >
                <RotateCcw size={14} />
                重置
              </button>

              {/* 删除 */}
              <button
                onClick={() => removeImage(selectedImage.id)}
                className="w-full py-2 text-xs text-red-300 hover:text-red-200 bg-red-900/30 hover:bg-red-800/40 border border-red-700/40 rounded-lg transition-all flex items-center justify-center gap-1"
              >
                <Trash2 size={14} />
                删除
              </button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default ImageComposer;
